import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { Preview as PreviewIcon, Code as VariablesIcon, ContentCopy as ContentCopyIcon, Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTeam } from '../../context/TeamContext';
import { sendEmail, renderEmailTemplate, emailTemplateService, teamsService } from '../../services/api';

const EmailSettings = () => {
  const { selectedTeam } = useTeam();
  console.log('📧 EmailSettings - Rendu avec selectedTeam:', selectedTeam);
  
  // Forcer une re-exécution quand selectedTeam devient disponible
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
  
  // États locaux stables
  const [settings, setSettings] = useState({
    senderName: '',
    subject: '',
    htmlBody: '',
    ccEmails: '',
    bccEmails: '',
    sendTime: '08:00'
  });
  
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [variablesOpen, setVariablesOpen] = useState(false);

  // Clé stable pour ReactQuill
  const quillKey = useMemo(() => 
    `email-settings-${selectedTeam?.id || 'default'}`, 
    [selectedTeam?.id]
  );

  // Variables disponibles avec descriptions
  const availableVariables = useMemo(() => [
    {
      variable: '{{firstName}}',
      description: 'Prénom du technicien destinataire',
      example: 'Jean'
    },
    {
      variable: '{{lastName}}',
      description: 'Nom de famille du technicien destinataire', 
      example: 'Dupont'
    },
    {
      variable: '{{startDate}}',
      description: 'Date de début de la période d\'astreinte',
      example: '07 août 2025'
    },
    {
      variable: '{{endDate}}',
      description: 'Date de fin de la période d\'astreinte',
      example: '13 août 2025'
    },
    {
      variable: '{{skills}}',
      description: 'Compétences du technicien (liste séparée par virgules)',
      example: 'ALCATEL, MITEL, CISCO'
    },
    {
      variable: '{{primaryTeamName}}',
      description: 'Nom de l\'équipe principale',
      example: 'IPS Alsace Lorraine'
    },
    {
      variable: '{{supportTeamNames}}',
      description: 'Noms des équipes de soutien',
      example: 'IPS BFC, IPS Centre'
    },
    {
      variable: '{{#teamsColumns}}...{{/teamsColumns}}',
      description: 'Bloc de tableau dynamique avec colonnes pour équipes principale et soutien',
      example: 'Génère un tableau avec colonnes équipes'
    },
    {
      variable: '{{#entries}}...{{/entries}}',
      description: 'Boucle sur les techniciens d\'une équipe (à utiliser dans teamsColumns)',
      example: 'Liste les techniciens avec leurs infos'
    },
    {
      variable: '{{technicianName}}',
      description: 'Nom du technicien (à utiliser dans une boucle entries)',
      example: 'Jean Dupont'
    },
    {
      variable: '{{contact}}',
      description: 'Informations de contact du technicien',
      example: 'jean.dupont@example.com ou +33 6 XX XX XX XX'
    }
  ], []);

  // Gestionnaire pour copier une variable
  const handleCopyVariable = useCallback((variable) => {
    navigator.clipboard.writeText(variable).then(() => {
      setSuccess(`Variable "${variable}" copiée !`);
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Erreur lors de la copie');
    });
  }, []);

  // Gestionnaire de chargement du template DI2E
  const handleLoadDI2ETemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await emailTemplateService.getTemplate();
      const htmlContent = response?.template;
      
      if (htmlContent) {
        setSettings(prev => ({
          ...prev,
          htmlBody: htmlContent,
          subject: prev.subject || 'Info astreinte à partir de ce soir 18H',
          senderName: prev.senderName || `Astreintes ${selectedTeam?.name || ''}`
        }));
        setSuccess('Template DI2E chargé avec succès !');
      } else {
        setError('Template DI2E non trouvé côté serveur');
      }
    } catch (err) {
      console.error('Erreur lors du chargement du template:', err);
      setError('Erreur lors du chargement du template DI2E');
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?.name]);



  // Gestionnaire d'aperçu
  const handlePreview = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const mockCalendarEvent = {
        start: '2025-08-07T00:00:00.000Z',
        end: '2025-08-13T23:59:59.999Z',
      };

      // Utiliser directement settings.htmlBody pour l'aperçu, sans reconstruction
      const htmlForPreview = settings.htmlBody || '<p>Aucun contenu à prévisualiser</p>';

      const response = await renderEmailTemplate(
        htmlForPreview,
        {
          calendarEvent: mockCalendarEvent,
          teamId: selectedTeam?.id || 1,
          technicianId: 'mock-tech-id'
        }
      );

      setPreviewHtml(response.html || settings.htmlBody);
      setPreviewOpen(true);
    } catch (err) {
      console.error('Erreur aperçu:', err);
      setError('Erreur lors de la génération de l\'aperçu');
    } finally {
      setLoading(false);
    }
  }, [settings.htmlBody, selectedTeam?.id]);

  // Gestionnaire de sauvegarde
  const handleSave = useCallback(async () => {
    try {
      const payload = {
        emailSettings: {
          senderName: settings.senderName,
          subject: settings.subject,
          ccEmails: settings.ccEmails,
          bccEmails: settings.bccEmails,
          htmlTemplate: settings.htmlBody || '',
          sendTime: settings.sendTime || '08:00'
        }
      };
      await teamsService.updateSettings(selectedTeam.id, payload);
      setSuccess('Paramètres sauvegardés en base avec succès !');
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde des paramètres');
    }
  }, [settings, selectedTeam?.id]);

  // Gestionnaire de test d'envoi
  const handleSendTest = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Le backend utilise sa propre configuration SMTP côté serveur
      // Plus besoin d'envoyer les paramètres SMTP depuis le frontend
      const testData = {
        to: 'admin@provserver.ovh',
        from: `${settings.senderName} <admin@provserver.ovh>`,
        subject: settings.subject,
        html: settings.htmlBody,
        calendarEvent: {
          start: '2025-08-07T00:00:00.000Z',
          end: '2025-08-13T23:59:59.999Z'
        },
        technicianId: 'test-tech-id',
        teamId: selectedTeam?.id || 1
      };

      await sendEmail(testData);
      setSuccess('Email de test envoyé avec succès !');
    } catch (err) {
      console.error('Erreur lors de l\'envoi du test:', err);
      setError('Erreur lors de l\'envoi de l\'email de test');
    } finally {
      setLoading(false);
    }
  }, [settings, selectedTeam?.id]);

  // Gestionnaires de changement stables
  const handleSenderNameChange = useCallback((event) => {
    setSettings(prev => ({ ...prev, senderName: event.target.value }));
  }, []);

  const handleSubjectChange = useCallback((event) => {
    setSettings(prev => ({ ...prev, subject: event.target.value }));
  }, []);

  const handleCcEmailsChange = useCallback((event) => {
    setSettings(prev => ({ ...prev, ccEmails: event.target.value }));
  }, []);

  const handleBccEmailsChange = useCallback((event) => {
    setSettings(prev => ({ ...prev, bccEmails: event.target.value }));
  }, []);

  const handleSendTimeChange = useCallback((event) => {
    setSettings(prev => ({ ...prev, sendTime: event.target.value }));
  }, []);

  const handleHtmlBodyChange = useCallback((value) => {
    setSettings(prev => ({ ...prev, htmlBody: value || '' }));
  }, []);

  

  // Chargement initial des paramètres (API + migration éventuelle du localStorage)
  useEffect(() => {
    console.log('📧 EmailSettings - useEffect déclenché, selectedTeam:', selectedTeam, 'hasLoadedSettings:', hasLoadedSettings);
    if (!selectedTeam?.id || hasLoadedSettings) {
      console.log('📧 EmailSettings - Skip:', !selectedTeam?.id ? 'pas d\'équipe' : 'déjà chargé');
      return;
    }
    
    let alive = true;
    async function loadTeamSettings() {
      try {
        console.log('📧 EmailSettings - Chargement des paramètres pour l\'équipe:', selectedTeam.id, selectedTeam.name);
        setLoading(true);
        setError('');
        const apiSettings = await teamsService.getSettings(selectedTeam.id);
        console.log('📧 EmailSettings - Paramètres reçus de l\'API:', apiSettings);
        const email = apiSettings?.emailSettings || {};
        let htmlTemplate = email.htmlTemplate || '';
        // Logique de chargement du template
        let finalHtmlBody = htmlTemplate;
        
        // Si le template en base semble corrompu ou dupliqué, le nettoyer
        if (finalHtmlBody && finalHtmlBody.includes('Infrastructure & Services Numériques') && finalHtmlBody.includes('<div class="content">')) {
          // Détecter si c'est un template avec contenu dupliqué dans .content
          const contentMatch = finalHtmlBody.match(/<div[^>]*class=["']content["'][^>]*>([\s\S]*?)<\/div>/i);
          if (contentMatch && contentMatch[1] && contentMatch[1].includes('Infrastructure & Services Numériques')) {
            console.log('📧 EmailSettings - Template corrompu détecté, nettoyage...');
            // Charger le template DI2E propre
            try {
              const defaultTemplate = await emailTemplateService.getTemplate();
              finalHtmlBody = defaultTemplate?.template || '';
              console.log('📧 EmailSettings - Template DI2E propre rechargé');
              
              // Nettoyer le template des tirets orphelins et autres éléments indésirables
              finalHtmlBody = finalHtmlBody
                .replace(/--\s*<br\s*\/?>/gi, '') // Retirer les tirets suivis de br
                .replace(/--\s*<\/p>/gi, '</p>') // Retirer les tirets en fin de paragraphe
                .replace(/<p[^>]*>--\s*<\/p>/gi, '') // Retirer les paragraphes contenant seulement des tirets
                .replace(/^\s*--\s*$/gm, '') // Retirer les lignes contenant seulement des tirets
                .replace(/--\s*(?=<)/g, ''); // Retirer les tirets avant des tags HTML
              
              // Sauvegarder immédiatement le template propre en base
              const cleanPayload = {
                emailSettings: {
                  senderName: email.senderName || `Astreintes ${selectedTeam.name}`,
                  subject: email.subject || 'Info astreinte à partir de ce soir 18H',
                  ccEmails: email.ccEmails || '',
                  bccEmails: email.bccEmails || '',
                  htmlTemplate: finalHtmlBody,
                  sendTime: email.sendTime || '08:00'
                }
              };
              await teamsService.updateSettings(selectedTeam.id, cleanPayload);
              console.log('📧 EmailSettings - Template propre et nettoyé sauvegardé en base');
            } catch (e) {
              console.warn('Impossible de charger le template par défaut:', e);
            }
          }
        }
        
        // Si aucun template en base ou si on vient de le nettoyer, charger le template DI2E par défaut
        if (!finalHtmlBody || finalHtmlBody.trim() === '') {
          try {
            const defaultTemplate = await emailTemplateService.getTemplate();
            finalHtmlBody = defaultTemplate?.template || '';
            console.log('📧 EmailSettings - Template DI2E par défaut chargé');
          } catch (e) {
            console.warn('Impossible de charger le template par défaut:', e);
          }
        }
        
        const teamDefaults = {
          senderName: email.senderName || `Astreintes ${selectedTeam.name}`,
          subject: email.subject || 'Info astreinte à partir de ce soir 18H',
          htmlBody: finalHtmlBody,
          ccEmails: email.ccEmails || '',
          bccEmails: email.bccEmails || '',
          sendTime: email.sendTime || '08:00'
        };
        if (alive) {
          console.log('📧 EmailSettings - Paramètres appliqués:', teamDefaults);
          setSettings(prev => ({ ...prev, ...teamDefaults }));
          setHasLoadedSettings(true);
        }
      } catch (e) {
        console.error('⚠️ Erreur chargement paramètres équipe:', e);
        console.warn('⚠️ Paramètres équipe non disponibles, fallback localStorage');
      } finally {
        if (alive) setLoading(false);
      }
    }
    loadTeamSettings();
    return () => { alive = false; };
  }, [selectedTeam?.id, selectedTeam?.name, hasLoadedSettings]);
  
  // Réinitialiser le flag de chargement quand l'équipe change
  useEffect(() => {
    if (selectedTeam?.id) {
      setHasLoadedSettings(false);
    }
  }, [selectedTeam?.id]);

  // Nettoyage des messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
        Paramètres Email
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom de l'expéditeur"
              value={settings.senderName}
              onChange={handleSenderNameChange}
              placeholder="Astreintes DI2E"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Heure d'envoi des emails"
              type="time"
              value={settings.sendTime}
              onChange={handleSendTimeChange}
              InputLabelProps={{ shrink: true }}
              helperText="Heure d'envoi automatique des emails d'astreinte"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sujet de l'email"
              value={settings.subject}
              onChange={handleSubjectChange}
              placeholder="Info astreinte à partir de ce soir 18H"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Emails en copie (CC)"
              value={settings.ccEmails}
              onChange={handleCcEmailsChange}
              placeholder="manager@example.com, team@example.com"
              helperText="Séparez les emails par des virgules"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Emails en copie cachée (BCC)"
              value={settings.bccEmails}
              onChange={handleBccEmailsChange}
              placeholder="archive@example.com"
              helperText="Séparez les emails par des virgules"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Template Email
              </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<VariablesIcon />}
              onClick={() => setVariablesOpen(true)}
              size="medium"
            >
              Variables
            </Button>
            <Button
              variant="contained"
              onClick={handleLoadDI2ETemplate}
              disabled={loading}
              sx={{ 
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                }
              }}
            >
              Modèle DI2E
            </Button>
          </Box>
        </Box>



          <Box sx={{ '& .ql-editor': { minHeight: '300px' } }}>
            <ReactQuill
              key={quillKey}
              value={settings.htmlBody || ''}
              onChange={handleHtmlBodyChange}
              placeholder="Contenu de l'email..."
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                  [{ 'font': [] }, { 'size': ['small', false, 'large', 'huge'] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'script': 'sub'}, { 'script': 'super' }],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                  [{ 'align': [] }],
                  ['blockquote', 'code-block'],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              formats={[
                'header', 'font', 'size',
                'bold', 'italic', 'underline', 'strike', 'blockquote',
                'list', 'bullet', 'indent',
                'link', 'image',
                'align', 'color', 'background',
                'script', 'code-block'
              ]}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              disabled={loading || !settings.htmlBody?.trim()}
            >
              Aperçu
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              color="success"
            >
              Sauvegarder
            </Button>

            <Button
              variant="outlined"
              onClick={handleSendTest}
              disabled={loading}
              color="info"
            >
              Envoyer un test
            </Button>
          </Box>
      </Paper>

      {/* Dialog d'aperçu */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Aperçu de l'email
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: 1,
              p: 2,
              maxHeight: '500px',
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog des variables */}
      <Dialog
        open={variablesOpen}
        onClose={() => setVariablesOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VariablesIcon />
            Variables disponibles pour l'email
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cliquez sur une variable pour la copier dans le presse-papiers, puis collez-la dans votre template.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Variable</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Exemple</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableVariables.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1, display: 'inline-block' }}>
                        {item.variable}
                      </Box>
                    </TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      <Box sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {item.example}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Copier cette variable">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyVariable(item.variable)}
                          color="primary"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
              💡 Conseils d'utilisation :
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>Variables simples</strong> : Utilisez directement (ex: {'{'}{'{'} firstName {'}'}{'}'} )<br/>
              • <strong>Blocs dynamiques</strong> : Utilisez {'{'}{'{'} #teamsColumns {'}'}{'}'} pour créer des tableaux<br/>
              • <strong>Boucles</strong> : Utilisez {'{'}{'{'} #entries {'}'}{'}'} pour lister les techniciens<br/>
              • <strong>Format</strong> : Respectez exactement la syntaxe avec les accolades doubles
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariablesOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
          </Box>
  );
};

export default EmailSettings; 